import {
  Box,
  LinkBox,
  LinkOverlay,
  List,
  ListItem,
  Separator,
} from '@chakra-ui/react';
import { ApiListResponse } from '@monetize/types/common';
import { FC, Fragment, MutableRefObject, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MBox, MFlex, MHStack } from '../chakra';
import { MTooltip } from '../MTooltip';
import MText from '../MText';
import { Highlighter } from './Highlighter';
import { SearchedItemType } from './MSearchInput';
import { useKeyPress } from './useKeyPress';
import { sortSearchRecordByStatus } from './utils';

interface SearchedListProps {
  debouncedValue: string;
  nameField: string;
  isFetching: boolean;
  cursor: number;
  data: ApiListResponse<SearchedItemType>;
  itemRefs: MutableRefObject<{
    [index: number]: HTMLAnchorElement;
  }>;
  setCursorAndScroll: (index: number) => void;

  handleReset: () => void;
  generateRoute: (id: string) => string;
}

export const SearchedList: FC<SearchedListProps> = ({
  debouncedValue,
  nameField,
  isFetching,
  data,
  cursor,
  itemRefs,
  setCursorAndScroll,

  handleReset,
  generateRoute,
}) => {
  const arrowUpPressed = useKeyPress('ArrowUp');
  const arrowDownPressed = useKeyPress('ArrowDown');
  const enterPressed = useKeyPress('Enter');

  useEffect(() => {
    if (arrowUpPressed) {
      const nextIndex = cursor !== 0 ? cursor - 1 : data.content.length - 1;
      setCursorAndScroll(nextIndex);
    }
  }, [arrowUpPressed, data]);

  useEffect(() => {
    if (arrowDownPressed) {
      const nextIndex = cursor !== data.content.length - 1 ? cursor + 1 : 0;
      setCursorAndScroll(nextIndex);
    }
  }, [arrowDownPressed, data]);

  useEffect(() => {
    if (
      enterPressed &&
      typeof itemRefs.current?.[cursor]?.click === 'function'
    ) {
      itemRefs.current?.[cursor].click();
    }
  }, [enterPressed, cursor]);

  const searchWords = debouncedValue.split(/\s/).filter((word) => word);
  const records = data?.content || [];
  const statuMap = {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    DRAFT: 'Draft',
    ARCHIVED: 'Archived',
    CANCELED: 'Canceled',
    SUSPENDED: 'Suspended',
    // Invoice statuses, surfaced by invoice number search
    UNPAID: 'Unpaid',
    PAID: 'Paid',
    REVERSED: 'Reversed',
    VOID: 'Void',
  };

  return (
    // v2's bare `List`: no bullets and no indent. `variant="plain"` would
    // make each item inline-flex and put the name and ID/status on one line.
    <List.Root maxH="30rem" overflowY="auto" listStyleType="none" ms="0">
      {!isFetching &&
        sortSearchRecordByStatus(records).map(
          ({ id, customId, [nameField]: name, status }, index) => {
            return (
              <Fragment key={id}>
                <LinkBox
                  borderRadius="3"
                  p="2"
                  tabIndex={-1}
                  cursor="pointer"
                  role="presentation"
                  _hover={{ background: 'tGray.support' }}
                  background={
                    index === cursor ? 'tGray.support' : 'transparent'
                  }
                  asChild
                >
                  <ListItem>
                    <MHStack>
                      <LinkOverlay asChild>
                        <Link
                          to={generateRoute(id)}
                          ref={(element: HTMLAnchorElement) => {
                            itemRefs.current[index] = element;
                          }}
                          onClick={() => handleReset()}
                        >
                          <Highlighter
                            textToHighlight={name}
                            searchWords={searchWords}
                            fontWeight="500"
                          />
                        </Link>
                      </LinkOverlay>
                    </MHStack>
                    <MFlex justify="space-between" align="center" gap="2">
                      <MBox>
                        <Highlighter
                          color="tGray.darkPurple"
                          textToHighlight={id}
                          searchWords={searchWords}
                          fontSize="xs"
                        />
                        {customId && (
                          <MTooltip label="Custom ID" placement="bottom-start">
                            <MBox
                              asChild
                              display="inline-block"
                              position="relative"
                              zIndex={1}
                            >
                              <Link
                                to={generateRoute(id)}
                                onClick={() => handleReset()}
                              >
                                <Highlighter
                                  color="tGray.darkPurple"
                                  textToHighlight={customId}
                                  searchWords={searchWords}
                                  fontSize="xs"
                                />
                              </Link>
                            </MBox>
                          </MTooltip>
                        )}
                      </MBox>
                      {status && (
                        <MText color="tGray.darkPurple" fontSize="xs">
                          {statuMap[status]}
                        </MText>
                      )}
                    </MFlex>
                  </ListItem>
                </LinkBox>

                {records.length > 1 && records.length !== index + 1 && (
                  <Box pr="2">
                    <Separator mx="2" />
                  </Box>
                )}
              </Fragment>
            );
          },
        )}
    </List.Root>
  );
};
